<template>
  <div id="contenedor-eliminar-contrato">
      <v-card>
      <v-card-title class="secondary--text"> Eliminar Contrato </v-card-title>
      <v-card-text  v-if="!$apollo.loading">
        <v-row>
          <span
            v-if="!this.tieneDependencias"
            class="secondary--text">
            ¿Desea eliminar el contrato <strong>{{contrato.numero}}</strong>?
          </span>
          <span
            v-else
            class="secondary--text mb-2">
            El Contrato <strong>{{contrato.numero}}</strong> no pude ser eliminado debido a
            que cuenta con registros en los siguientes apartados:
          </span>
        </v-row>
        <v-row justify="start" align="center">
          <template
            v-for="(dato, index) in datosColumnas">
            <v-col
              v-if="verificarDependencias(dato.valor)"
              :key="index"
              cols="6"
              class="red--text font-weight-bold ma-0 pa-0">
                {{ dato.texto }}
            </v-col>
          </template>

          <!-- Asignado como contrato relacionado -->
          <template
            v-for="(asignado, index) in asignadoComoContratoRelacionado">
            <v-col
              :key="`asignado-${index}`"
              cols="6"
              class="red--text font-weight-bold ma-0 pa-0">
                Relacionado al contrato {{ asignado.contrato.numero }}
            </v-col>
          </template>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <custom-button
          @click="cerrar"
          tipo="formCancelar"
          label="Cancelar"
          class="mr-3"
        >
        </custom-button>
        <custom-button
          @click="mixEliminarContrato"
          :disabled="enviandoFormulario || tieneDependencias || $apollo.loading"
          :loading="enviandoFormulario"
          tipo="formAceptar"
          label="Eliminar"
        >
        </custom-button>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import CONTRATO from '@/graphql/querys/administracion/contratos/contrato.gql';
import ACTIVIDADES from '@/graphql/querys/administracion/actividades/actividades.gql';
import CONTRATOS_RELACIONADOS from '@/graphql/querys/administracion/contratos/contratosRelacionados/contratosRelacionados.gql';
import mixContratos from './mixContratos';
import CustomButton from '@/components/buttons/Button.vue';

export default {
  name: 'EliminarContrato',
  mixins: [mixContratos],
  props: {
    contratoEliminar: {
      type: Object,
      required: true,
    },
  },
  components: {
    CustomButton,
  },
  data() {
    return {
      formularioValido: false,
      enviandoFormulario: false,
      datosColumnas: [
        { valor: 'contratosRelacionados', texto: 'Contratos Relacionados' },
        { valor: 'actividades', texto: 'Actividades' },
        { valor: 'expediente', texto: 'Expediente' },
        { valor: 'reportesFisicos', texto: 'Reportes Fisicos' },
        { valor: 'estimaciones', texto: 'Estimaciones' },
        { valor: 'solicitudesPagos', texto: 'Solicitudes de Pago' },
        { valor: 'gastos', texto: 'Gastos' },
        { valor: 'escalatorias', texto: 'Escalatorias' },
        { valor: 'anticipos', texto: 'Anticipos' },
        { valor: 'convenios', texto: 'Convenios' },
        { valor: 'fianzas', texto: 'Fianzas' },
        { valor: 'catalogoConceptos', texto: 'Catalogo de Conceptos' },
        { valor: 'deducciones', texto: 'Deducciones' },
        { valor: 'programas', texto: 'Programa' },
        { valor: 'responsables', texto: 'Responsables' },
        { valor: 'superintendentes', texto: 'Superintendentes' },
      ],
    };
  },
  apollo: {
    contrato: {
      query: CONTRATO,
      variables() {
        return {
          id: this.contratoEliminar.id,
        };
      },
    },
    actividades: {
      query: ACTIVIDADES,
      variables() {
        return {
          idReferencia: this.contratoEliminar.id,
        };
      },
    },
    contratosRelacionados: {
      query: CONTRATOS_RELACIONADOS,
    },
  },
  methods: {
    verificarDependencias(dato) {
      const existeDependencia = this.dependenciasDeContrato.includes(dato);
      return existeDependencia;
    },
    cerrar() {
      this.$emit('cerrar');
    },
  },
  computed: {
    getContratosRelacionados() {
      if (!this.contratosRelacionados) return [];
      return this.contratosRelacionados;
    },
    getActividades() {
      if (!this.actividades) return [];
      return this.actividades;
    },
    tieneDependencias() {
      return this.dependenciasDeContrato.length > 0;
    },
    asignadoComoContratoRelacionado() {
      // contratos relacionados donde el contrato a eliminar este asignado
      const asignadoContratoRelacionado = this.getContratosRelacionados
        .filter((contratoRelacionado) => contratoRelacionado.idContratoRelacionado
        === this.contratoEliminar.id);
      return asignadoContratoRelacionado;
    },
    dependenciasDeContrato() {
      if (!this.contrato) return [];

      const dependenciasContrato = [];

      // Verificar si tiene anticipos
      if (this.contrato.anticipos.length > 0) {
        dependenciasContrato.push('anticipos');
      }

      // Verificar si tiene convenios
      if (this.contrato.convenios.length > 0) {
        dependenciasContrato.push('convenios');
      }

      // Verificar si tiene fianzas
      if (this.contrato.fianzas.length > 0) {
        dependenciasContrato.push('fianzas');
      }

      // Verificar si tiene catalogo de conceptos
      if (this.contrato.catalogoConceptos.length > 0) {
        dependenciasContrato.push('catalogoConceptos');
      }

      // Verificar si tiene deducciones
      if (this.contrato.deducciones.length > 0) {
        dependenciasContrato.push('deducciones');
      }

      // Verificar si tiene programa
      if (this.contrato.programas.length > 0) {
        dependenciasContrato.push('programas');
      }

      // Verificar si tiene responsables registrados
      if (this.contrato.responsables.length > 0) {
        dependenciasContrato.push('responsables');
      }

      // Verificar si tiene superintendente registrado
      if (this.contrato.superintendentes.length > 0) {
        dependenciasContrato.push('superintendentes');
      }

      // Verificar si tiene contratos relacionados registrados
      if (this.contrato.contratosRelacionados.length > 0) {
        dependenciasContrato.push('contratosRelacionados');
      }

      // Verificar si tiene actividades registradas
      if (this.getActividades.length > 0) {
        dependenciasContrato.push('actividades');
      }

      // Verificar si tiene expediente registrado
      if (this.contrato.expediente.length > 0) {
        dependenciasContrato.push('expediente');
      }

      // Verificar si tiene reportes fisicos registrados
      if (this.contrato.reportesFisicos.length > 0) {
        dependenciasContrato.push('reportesFisicos');
      }

      // Verificar si tiene estimaciones registradas
      if (this.contrato.estimaciones.length > 0) {
        dependenciasContrato.push('estimaciones');
      }

      // Verificar si tiene solicitudes de pago registradas
      if (this.contrato.solicitudesPagos.length > 0) {
        dependenciasContrato.push('solicitudesPagos');
      }

      // Verificar si tiene gastos registrados
      if (this.contrato.gastos.length > 0) {
        dependenciasContrato.push('gastos');
      }

      // Verificar si tiene escalatorias registradas
      if (this.contrato.escalatorias.length > 0) {
        dependenciasContrato.push('escalatorias');
      }

      // Verificar si esta asignado como contrato relacionado en algun contrato
      if (this.asignadoComoContratoRelacionado.length > 0) {
        dependenciasContrato.push('asignadoComoContratoRelacionado');
      }
      return dependenciasContrato;
    },
  },
  async mounted() {
    await this.$apollo.queries.contrato.refetch({
      id: this.contratoEliminar.id,
    });
    await this.$apollo.queries.contratosRelacionados.refetch();
  },
};
</script>

<style>
</style>
